import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useCommunity } from "@/context/CommunityContext";

function Table({ head, children, testId }) {
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border bg-white" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-[var(--brand-soft)] text-left text-xs uppercase tracking-wide">
            {head.map((h) => (
              <th key={h} className="px-4 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/admin/users", { params: { q: query } }).then(({ data }) => setUsers(data || [])).catch((e) => setError(errMsg(e)));
  }, [query]);
  useEffect(load, [load]);

  const update = async (user, patch) => {
    setError("");
    try {
      await api.put(`/admin/users/${user.id}`, patch);
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="text-3xl">Benutzer</h1>
      <input className="field mt-4 max-w-sm" placeholder="Name suchen" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="user-search" />
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
      <Table head={["Name", "E-Mail", "Rolle", "Testkonto"]} testId="users-table">
        {users.map((user) => (
          <tr key={user.id} className="border-b last:border-0">
            <td className="px-4 py-3">{user.display_name}</td>
            <td className="px-4 py-3 text-xs">{user.email}</td>
            <td className="px-4 py-3">
              <select className="field !py-1.5 !text-xs" value={user.role || "REGISTERED_USER"} onChange={(e) => update(user, { role: e.target.value })} data-testid={`user-role-${user.id}`}>
                {["REGISTERED_USER", "MODERATOR", "COMMUNITY_ADMIN", "PLATFORM_ADMIN"].map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </td>
            <td className="px-4 py-3">
              <input type="checkbox" checked={!!user.is_test_user} onChange={(e) => update(user, { is_test_user: e.target.checked })} data-testid={`user-test-${user.id}`} />
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function StatusList({ endpoint, title, statuses, fields, testId }) {
  const { community } = useCommunity();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .get(`/admin/${endpoint}`, { params: community ? { community_id: community.id } : {} })
      .then(({ data }) => setItems(data || []))
      .catch((e) => setError(errMsg(e)));
  }, [endpoint, community]);
  useEffect(load, [load]);

  const update = async (item, patch) => {
    setError("");
    try {
      await api.put(`/admin/${endpoint}/${item.id}`, patch);
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div data-testid={testId}>
      <h1 className="text-3xl">{title}</h1>
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
      <Table head={[...fields.map((f) => f.label), "Status"]}>
        {items.length === 0 ? (
          <tr><td className="px-4 py-6 text-[var(--ink-soft)]" colSpan={fields.length + 1}>Keine Einträge.</td></tr>
        ) : null}
        {items.map((item) => (
          <tr key={item.id} className="border-b last:border-0" data-testid={`${testId}-row-${item.id}`}>
            {fields.map((field) => (
              <td key={field.name} className="px-4 py-3 align-top">{String(item[field.name] ?? "—")}</td>
            ))}
            <td className="px-4 py-3">
              <select className="field !py-1.5 !text-xs" value={item.status || statuses[0]} onChange={(e) => update(item, { status: e.target.value })} data-testid={`${testId}-status-${item.id}`}>
                {statuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export const AdminIdeas = () => (
  <StatusList
    endpoint="ideas"
    title="Ideen aus der Gemeinde"
    statuses={["NEW", "REVIEWING", "PLANNED", "IMPLEMENTED", "REJECTED"]}
    fields={[
      { name: "idea_type", label: "Typ" },
      { name: "title", label: "Titel" },
      { name: "location_text", label: "Ort" },
      { name: "description", label: "Beschreibung" },
      { name: "author_name", label: "Von" },
    ]}
    testId="admin-ideas"
  />
);

export const AdminReports = () => (
  <StatusList
    endpoint="problem-reports"
    title="Meldungen"
    statuses={["NEW", "IN_PROGRESS", "RESOLVED", "CLOSED"]}
    fields={[
      { name: "subject_type", label: "Bereich" },
      { name: "message", label: "Meldung" },
      { name: "created_at", label: "Zeitpunkt" },
    ]}
    testId="admin-reports"
  />
);

export function AdminConfirmations() {
  const { community } = useCommunity();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .get("/admin/confirmations", { params: community ? { community_id: community.id } : {} })
      .then(({ data: payload }) => setData(payload))
      .catch((e) => setError(errMsg(e)));
  }, [community]);
  useEffect(load, [load]);

  const confirm = async (entryId, approve) => {
    setError("");
    try {
      await api.post(`/challenge-entries/${entryId}/confirm`, { approve });
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-confirmations">
      <h1 className="text-3xl">Bestätigungen</h1>
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
      <h2 className="mt-6 text-xl">Challenge-Ergebnisse</h2>
      <Table head={["Benutzer", "Challenge", "Ergebnis", "Aktion"]}>
        {(data?.challenge_entries || []).length === 0 ? (
          <tr><td className="px-4 py-6 text-[var(--ink-soft)]" colSpan={4}>Nichts offen.</td></tr>
        ) : null}
        {(data?.challenge_entries || []).map((entry) => (
          <tr key={entry.id} className="border-b last:border-0">
            <td className="px-4 py-3">{entry.user_name}</td>
            <td className="px-4 py-3">{entry.challenge_title}</td>
            <td className="px-4 py-3">{entry.result_value} {entry.unit_label || ""}</td>
            <td className="px-4 py-3">
              <div className="flex gap-2">
                <button className="btn btn-primary !py-1.5 !text-xs" onClick={() => confirm(entry.id, true)} data-testid={`admin-confirm-${entry.id}`}>BESTÄTIGEN</button>
                <button className="btn btn-ghost !py-1.5 !text-xs" onClick={() => confirm(entry.id, false)}>ABLEHNEN</button>
              </div>
            </td>
          </tr>
        ))}
      </Table>
      <h2 className="mt-8 text-xl">Offene Matches</h2>
      <Table head={["Spiel", "Spieler", "Ergebnis"]}>
        {(data?.matches || []).length === 0 ? (
          <tr><td className="px-4 py-6 text-[var(--ink-soft)]" colSpan={3}>Nichts offen.</td></tr>
        ) : null}
        {(data?.matches || []).map((match) => (
          <tr key={match.id} className="border-b last:border-0">
            <td className="px-4 py-3">{match.game_title}</td>
            <td className="px-4 py-3">{match.player_a_name} vs {match.player_b_name}</td>
            <td className="px-4 py-3">{match.score_a} : {match.score_b}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function AdminLoveMessages() {
  const { community } = useCommunity();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .get("/admin/love-messages", { params: community ? { community_id: community.id } : {} })
      .then(({ data }) => setItems(data || []))
      .catch((e) => setError(errMsg(e)));
  }, [community]);
  useEffect(load, [load]);

  const update = async (item, patch) => {
    setError("");
    try {
      await api.put(`/admin/love-messages/${item.id}`, patch);
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-love-messages">
      <h1 className="text-3xl">Liebesbaum – Botschaften</h1>
      <p className="text-sm text-[var(--ink-soft)]">Konfiguration des Liebesbaums findest du unter „Liebesbaum-Einstellungen“.</p>
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
      <Table head={["Personen", "Botschaft", "Status", "Sichtbar"]}>
        {items.length === 0 ? (
          <tr><td className="px-4 py-6 text-[var(--ink-soft)]" colSpan={4}>Keine Botschaften.</td></tr>
        ) : null}
        {items.map((item) => (
          <tr key={item.id} className="border-b last:border-0" data-testid={`love-row-${item.id}`}>
            <td className="px-4 py-3">{item.author_name} & {item.partner_name}</td>
            <td className="max-w-md px-4 py-3">{item.message}</td>
            <td className="px-4 py-3 text-xs">{item.status}</td>
            <td className="px-4 py-3">
              <button className="btn btn-ghost !py-1.5 !text-xs" onClick={() => update(item, { hidden: !item.hidden })} data-testid={`love-hide-${item.id}`}>
                {item.hidden ? "EINBLENDEN" : "AUSBLENDEN"}
              </button>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
